<template>
  <div class="flex flex-col items-center justify-center max-w-md w-full mx-4 py-8 px-6 text-center space-y-6">
    <!-- No Access View -->
    <div v-if="!showRequestForm" class="space-y-6">
      <div class="flex rounded-full items-center justify-center">
        <div class="bg-white/10 rounded-full p-4">
          <LockIcon class="w-6 h-6 text-white" />
        </div>
      </div>

      <h1 class="text-2xl mb-4 text-white tracking-widest">YOU DON'T HAVE ACCESS TO THIS PAGE.</h1>

      <p class="text-muted-foreground mb-6">
        You don't have permission to view this page. You can request access from the account administrator.
      </p>

      <Button class="w-full mb-4" data-testid="no-access-request-button" @click="showRequestForm = true">Request Access</Button>

      <template v-if="!isLoggedIn">
        <Separator label="Or" class="separator mb-4 bg-muted-foreground" />

        <div class="flex">
          <Button variant="secondary" class="w-full" as-child>
            <NuxtLink :to="`/login?redirectedFrom=${encodeURIComponent($route.fullPath)}`">Sign In</NuxtLink>
          </Button>
        </div>
      </template>
    </div>

    <!-- Request Access Form -->
    <div v-else class="w-full space-y-6 border rounded-lg bg-[#FFFFFF0D] border-[#ffffff1a] p-6">
      <div class="text-center">
        <h2 class="mb-2 text-white tracking-widest">REQUEST ACCESS</h2>
        <p class="text-sm text-muted-foreground">Fill out the form to request access to this page</p>
      </div>

      <form class="space-y-4 w-full text-left" @submit.prevent="onSubmit">
        <FormField v-slot="{ componentField }" name="name">
          <FormItem>
            <FormLabel class="font-medium text-sm">Full Name</FormLabel>
            <FormControl>
              <Input type="text" placeholder="John Doe" v-bind="componentField" class="border" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormField v-slot="{ componentField }" name="email_address">
          <FormItem>
            <FormLabel class="font-medium text-sm">Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="your@email.com" v-bind="componentField" class="border" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormField v-slot="{ componentField }" name="reason">
          <FormItem>
            <FormLabel class="font-medium text-sm">Reason</FormLabel>
            <FormControl>
              <Textarea placeholder="Please provide a reason for your access request." v-bind="componentField" class="border" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <Button class="w-full" type="submit" data-testid="no-access-submit-button">Request Access</Button>

        <div class="flex flex-col w-full items-center justify-center space-y-4">
          <span v-if="!isLoggedIn" class="text-muted-foreground text-sm">
            Already have an account?
            <NuxtLink
              class="style-none text-primary text-base underline hover_opacity-80"
              :to="`/login?redirectedFrom=${encodeURIComponent($route.fullPath)}`"
              >Sign In</NuxtLink
            >
          </span>

          <Button variant="ghost" size="sm" class="text-muted-foreground flex items-center gap-x-1" @click="showRequestForm = false">
            <ArrowLeftIcon class="w-4 h-4" />
            Back
          </Button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod'
import z from 'zod'
import { LockIcon, ArrowLeftIcon } from 'lucide-vue-next'
import { useToast } from '~/components/ui/toast'

const props = defineProps<{
  contentType?: string
  contentId?: string | number
}>()

const { state: userState } = useUser()
const { requestDeckAccess } = useDecks()
const { toast } = useToast()

const isLoggedIn = computed(() => !!userState.value?.user)
const showRequestForm = ref(false)

const formSchema = toTypedSchema(
  z.object({
    name: z.string().min(1, 'Name is required'),
    email_address: z.string().email('Please enter a valid email address').min(1, 'Email is required'),
    reason: z.string().min(1, 'Reason is required'),
  }),
)

const { handleSubmit, resetForm } = useVrifyForm({
  validationSchema: formSchema,
  initialValues: {
    name: userState.value.user?.name ?? '',
    email_address: userState.value.user?.email_address ?? '',
    reason: '',
  },
})

watch(showRequestForm, newVal => {
  if (newVal) {
    resetForm()
  }
})

const onSubmit = handleSubmit(async values => {
  try {
    if (props.contentType === 'deck' && props.contentId) {
      await requestDeckAccess(props.contentId.toString(), {
        name: values.name,
        email: values.email_address,
        reason: values.reason,
      })
    }
    // Future: Add other content types here
  } finally {
    toast({
      title: 'Access Request Submitted',
      description: 'Your access request has been submitted. You will be notified via email when it is approved.',
    })
    showRequestForm.value = false
  }
})
</script>
